import Vue from "vue"

/**
 * ======================================================================
 *  STATE
 * ======================================================================
 */

const state = {
    auditQuestions: [],
    paginatedAuditQuestions: [],
    applicationAuditQuestions: [],
    auditCategories: [],
    paginatedAuditCategories: [],
    auditSettings: [],
    teams: [],
    paginatedApplicationAudits: [],
    form: {
        answers: {},
        comment: "",
        applicationId: "",
        userId: "",
        role: "",
        passed: "",
    },
}

/**
 * ======================================================================
 *  GETTERS
 * ======================================================================
 */

const getters = {

    // ====================================
    // Audit Questions
    // ====================================
    getAuditQuestions: state => {
        return state.auditQuestions
    },
    getApplicationAuditQuestions: state => {
        return state.applicationAuditQuestions
    },
    getPaginatedAuditQuestions: state => {
        return state.paginatedAuditQuestions
    },

    // ====================================
    // Audit Categories
    // ====================================
    getAuditCategories: state => {
        return state.auditCategories
    },
    getPaginatedAuditCategories: state => {
        return state.paginatedAuditCategories
    },

    // ====================================
    // Audit Settings
    // ====================================
    getAuditSettings: state => {
        return state.auditSettings
    },

    // ====================================
    // Teams
    // ====================================
    getTeams: state => {
        return state.teams
    },

    // ====================================
    // Application Audits
    // ====================================
    getPaginatedApplicationAudits: state => {
        return state.paginatedApplicationAudits
    },

    getForm: state => {
        return state.form
    }
}

/**
 * ======================================================================
 *  MUTATIONS
 * ======================================================================
 */

const mutations = {

    // ====================================
    // Audit Questions
    // ====================================
    setAuditQuestions(state, data) {
        state.auditQuestions = data
    },
    setPaginatedAuditQuestions(state, data) {
        state.paginatedAuditQuestions = data
    },
    setApplicationAuditQuestions(state, data) {
        state.applicationAuditQuestions = data
    },
    createAuditQuestion(state, data) {
        state.paginatedAuditQuestions.data.push(data)
    },
    updateAuditQuestion(state, data) {
        state.paginatedAuditQuestions.data = state.paginatedAuditQuestions.data.map(question => (question.uid == data.uid ? { ...question, ...data } : question))
    },
    deleteAuditQuestion(state, data) {
        let key = state.paginatedAuditQuestions.data.findIndex(question => question.uid === data.uid)
        Vue.delete(state.paginatedAuditQuestions.data, key);
    },

    // ====================================
    // Audit Categories
    // ====================================
    setAuditCategories(state, data) {
        state.auditCategories = data
    },
    setPaginatedAuditCategories(state, data) {
        state.paginatedAuditCategories = data
    },
    createAuditCategory(state, data) {
        state.paginatedAuditCategories.data.push(data)
    },
    updateAuditCategory(state, data) {
        state.paginatedAuditCategories.data = state.paginatedAuditCategories.data.map(category => (category.uid == data.uid ? { ...category, ...data } : category))
    },
    deleteAuditCategory(state, data) {
        let key = state.paginatedAuditCategories.data.findIndex(category => category.uid === data.uid)
        Vue.delete(state.paginatedAuditCategories.data, key);
    },

    // ====================================
    // Audit Settings
    // ====================================
    setAuditSettings(state, data) {
        state.auditSettings = data
    },

    // ====================================
    // Teams
    // ====================================
    setTeams(state, data) {
        state.teams = data
    },

    // ====================================
    // Application Audits
    // ====================================
    setPaginatedApplicationAudits(state, data) {
        state.paginatedApplicationAudits = data
    },

    setForm(state, data) {
        state.form = data
    },
}

/**
 * ======================================================================
 *  ACTIONS
 * ======================================================================
 */
const actions = {

    // ====================================
    // Audit Questions
    // ====================================
    setAuditQuestions({ dispatch, commit, state }, data) {
        return new Promise((resolve, reject) => {
            axios.post("/api/application-audit/get-audit-questions", data, {
                headers: { Authorization: `Bearer ${$store.getters.token}` }
            }).then(
                response => {
                    if (_.has(data, 'paginated')) {
                        commit("setPaginatedAuditQuestions", response.data)
                    } else {
                        commit("setAuditQuestions", response.data)
                    }
                    resolve(response)
                },
                error => {
                    reject(error)
                }
            )
        })
    },
    setApplicationAuditQuestions({ dispatch, commit, state }, data) {
        return new Promise((resolve, reject) => {
            axios.post("/api/application-audit/get-application-audit-questions", data, {
                headers: { Authorization: `Bearer ${$store.getters.token}` }
            }).then(
                response => {
                    commit("setApplicationAuditQuestions", response.data)
                    resolve(response)
                },
                error => {
                    reject(error)
                }
            )
        })
    },
    saveAuditQuestion({ dispatch, commit, state }, data) {
        return new Promise((resolve, reject) => {
            axios.post("/api/application-audit/save-audit-question", data, {
                headers: { Authorization: `Bearer ${$store.getters.token}` }
            }).then(
                response => {
                    if (_.has(data, 'form.id')) {
                        commit("updateAuditQuestion", response.data)
                    } else {
                        commit("createAuditQuestion", response.data)
                    }
                    dispatch("setAuditQuestions")
                    resolve(response)
                },
                error => {
                    reject(error)
                }
            )
        })
    },
    deleteAuditQuestion({ dispatch, commit, state }, data) {
        return new Promise((resolve, reject) => {
            axios.post("/api/application-audit/delete-audit-question", data, {
                headers: { Authorization: `Bearer ${$store.getters.token}` }
            }).then(
                response => {
                    if (response.data) {
                        commit("deleteAuditQuestion", response.data)
                        if (!response.data.tenantData) {
                            commit("createAuditQuestion", response.data)
                        }
                        dispatch("setAuditQuestions")
                    }
                    resolve(response)
                },
                error => {
                    reject(error)
                }
            )
        })
    },
    copyAuditQuestion({ dispatch, commit, state }, data) {
        return new Promise((resolve, reject) => {
            axios.post("/api/application-audit/copy-audit-question", data, {
                headers: { Authorization: `Bearer ${$store.getters.token}` }
            }).then(
                response => {
                    if (data.master) {
                        commit("deleteAuditQuestion", response.data)
                        commit("createAuditQuestion", response.data)
                    } else {
                        commit("createAuditQuestion", response.data)
                    }
                    dispatch("setAuditQuestions")
                    resolve(response)
                },
                error => {
                    reject(error)
                }
            )
        })
    },
    setAuditQuestionState({ dispatch, commit, state }, data) {
        return new Promise((resolve, reject) => {
            axios.post("/api/application-audit/active-audit-question", data).then(
                response => {
                    commit("updateAuditQuestion", response.data)
                    dispatch("setAuditQuestions")
                    resolve(response)
                },
                error => {
                    reject(error)
                }
            )
        })
    },

    // ====================================
    // Audit Categories
    // ====================================
    setAuditCategories({ dispatch, commit, state }, data) {
        return new Promise((resolve, reject) => {
            axios.post("/api/application-audit/get-audit-categories", data, {
                headers: { Authorization: `Bearer ${$store.getters.token}` }
            }).then(
                response => {
                    if (_.has(data, 'paginated')) {
                        commit("setPaginatedAuditCategories", response.data)
                    } else {
                        commit("setAuditCategories", response.data)
                    }
                    resolve(response)
                },
                error => {
                    reject(error)
                }
            )
        })
    },
    saveAuditCategory({ dispatch, commit, state }, data) {
        return new Promise((resolve, reject) => {
            axios.post("/api/application-audit/save-audit-category", data, {
                headers: { Authorization: `Bearer ${$store.getters.token}` }
            }).then(
                response => {
                    if (_.has(data, 'form.id')) {
                        commit("updateAuditCategory", response.data)
                    } else {
                        commit("createAuditCategory", response.data)
                    }
                    dispatch("setAuditCategories")
                    resolve(response)
                },
                error => {
                    reject(error)
                }
            )
        })
    },
    deleteAuditCategory({ dispatch, commit, state }, data) {
        return new Promise((resolve, reject) => {
            axios.post("/api/application-audit/delete-audit-category", data, {
                headers: { Authorization: `Bearer ${$store.getters.token}` }
            }).then(
                response => {
                    if (response.data) {
                        commit("deleteAuditCategory", response.data)
                        if (!response.data.tenantData) {
                            commit("createAuditCategory", response.data)
                        }
                        dispatch("setAuditCategories")
                    }
                    resolve(response)
                },
                error => {
                    reject(error)
                }
            )
        })
    },
    copyAuditCategory({ dispatch, commit, state }, data) {
        return new Promise((resolve, reject) => {
            axios.post("/api/application-audit/copy-audit-category", data, {
                headers: { Authorization: `Bearer ${$store.getters.token}` }
            }).then(
                response => {
                    if (data.master) {
                        commit("deleteAuditCategory", response.data)
                        commit("createAuditCategory", response.data)
                    } else {
                        commit("createAuditCategory", response.data)
                    }
                    dispatch("setAuditCategories")
                    resolve(response)
                },
                error => {
                    reject(error)
                }
            )
        })
    },
    setAuditCategoryState({ dispatch, commit, state }, data) {
        return new Promise((resolve, reject) => {
            axios.post("/api/application-audit/active-audit-category", data, {
                headers: { Authorization: `Bearer ${$store.getters.token}` }
            }).then(
                response => {
                    commit("updateAuditCategory", response.data)
                    dispatch("setAuditCategories")
                    resolve(response)
                },
                error => {
                    reject(error)
                }
            )
        })
    },

    // ====================================
    // Audit Settings
    // ====================================
    setAuditSettings({ dispatch, commit, state }, data) {
        return new Promise((resolve, reject) => {
            axios.post("/admin/settings/application-audit/get-audit-settings", data, {
                headers: { Authorization: `Bearer ${$store.getters.token}` }
            }).then(
                response => {
                    commit("setAuditSettings", response.data)
                    resolve(response)
                },
                error => {
                    reject(error)
                }
            )
        })
    },
    saveAuditSettings({ dispatch, commit, state }, data) {
        return new Promise((resolve, reject) => {
            axios.post("/api/application-audit/save-audit-settings", data, {
                headers: { Authorization: `Bearer ${$store.getters.token}` }
            }).then(
                response => {
                    commit("setAuditSettings", response.data)
                    resolve(response)
                },
                error => {
                    reject(error)
                }
            )
        })
    },

    // ====================================
    // Teams
    // ====================================
    setTeams({ dispatch, commit, state }, data) {
        return new Promise((resolve, reject) => {
            axios.post("/api/application-audit/get-teams", data, {
                headers: { Authorization: `Bearer ${$store.getters.token}` }
            }).then(
                response => {
                    commit("setTeams", response.data)
                    resolve(response)
                },
                error => {
                    reject(error)
                }
            )
        })
    },

    // ====================================
    // Application Audits
    // ====================================
    setApplicationAudits({ dispatch, commit, state }, data) {
        return new Promise((resolve, reject) => {
            axios.post("/api/application-audit/get-application-audits", data, {
                headers: { Authorization: `Bearer ${$store.getters.token}` }
            }).then(
                response => {
                    commit("setPaginatedApplicationAudits", response.data)
                    resolve(response)
                },
                error => {
                    reject(error)
                }
            )
        })
    },

    setForm({ dispatch, commit, state }, data) {
        commit("setForm", data)
    },
}

export default {
    namespaced: true,
    state,
    getters,
    mutations,
    actions
}
